/**
 * @file IntegrationTransferStrip.tsx
 * @description Разовая полоса переноса на странице «Общие интеграции»
 * (phase19.28 этап А; uiux-platform-shops §6; PL13; platform.md §5
 * «Переходный период для Chaiberi»).
 *
 * Общие настройки видов «только своя» (СДЭК, Avito, VK, Telegram…) после
 * этапа А ни одному магазину не подставляются — их нужно перенести в
 * магазин-владельца. Полоса перечисляет такие настройки с кнопкой
 * «Перенести в магазин…»; пусто — полосы нет. Признак — `needsTransfer`.
 * Видна только при правах PLATFORM_MANAGE_INTEGRATIONS и PLATFORM_MANAGE_SHOPS
 * (их проверяет `useIntegrationTransfer`, окно — общее со строками интеграций).
 */

import { ArrowRightLeft } from "lucide-react";

import { INTEGRATION_KIND_META, type PlatformIntegrationDto } from "../../../lib/api-platform-integrations";
import { needsTransfer } from "../../../lib/platform/shop-integration-meta";
import { Button } from "../../ui/button";
import { NoticeStrip } from "../../ui/NoticeStrip";

interface Props {
  items: PlatformIntegrationDto[];
  /** Открыть окно переноса (`useIntegrationTransfer`); null — нет прав, полосы нет. */
  onTransfer: ((item: PlatformIntegrationDto) => void) | null;
}

export function IntegrationTransferStrip({ items, onTransfer }: Props) {
  const candidates = items.filter(needsTransfer);
  if (!onTransfer || candidates.length === 0) return null;
  const kinds = [...new Set(candidates.map((item) => INTEGRATION_KIND_META[item.kind]?.label ?? item.kind))];

  return (
    <>
      <NoticeStrip tone="warning" title="Общие настройки, которые должны принадлежать магазину">
        <p>
          {kinds.join(", ")} — виды «только своя»: общая настройка не подставляется ни одному магазину. Перенесите её в
          магазин, которому она принадлежит (например, Chaiberi).
        </p>
        <ul className="mt-2 flex flex-col gap-2">
          {candidates.map((item) => (
            <li key={item.id} className="flex flex-col gap-2 sm:flex-row sm:items-center">
              <span className="min-w-0 flex-1 break-words">
                {INTEGRATION_KIND_META[item.kind]?.label ?? item.kind} · {item.name}
              </span>
              <Button variant="soft" className="self-start sm:self-auto" onClick={() => onTransfer(item)}>
                <ArrowRightLeft className="size-4" aria-hidden />
                Перенести в магазин…
              </Button>
            </li>
          ))}
        </ul>
      </NoticeStrip>
    </>
  );
}
